import { getObjectActionDefinition } from "./catalog";
import type { ActionSurface, ObjectActionId, StaticActionShell } from "./types";

function insertStaticShellsAfter(
  base: StaticActionShell[],
  afterId: ObjectActionId,
  toInsert: StaticActionShell[],
): StaticActionShell[] {
  const insertIds = new Set(toInsert.map((shell) => shell.id));
  const filtered = base.filter((shell) => !insertIds.has(shell.id));
  const index = filtered.findIndex((shell) => shell.id === afterId);

  if (index === -1) {
    return [...filtered, ...toInsert];
  }

  return [
    ...filtered.slice(0, index + 1),
    ...toInsert,
    ...filtered.slice(index + 1),
  ];
}

/**
 * Merges base canvas-node shells with an entity-type extension (e.g. file-record).
 * Toolbar utilities stay anchored after `copy-url`; context menus lead with the extension block.
 */
export function mergeCanvasEntityShells(
  base: readonly StaticActionShell[],
  extension: readonly StaticActionShell[],
  surface: ActionSurface,
): StaticActionShell[] {
  if (extension.length === 0) {
    return [...base];
  }

  const extensionById = new Map(extension.map((shell) => [shell.id, shell]));
  const mergedBase = base.map((shell) => extensionById.get(shell.id) ?? shell);
  const baseIds = new Set(base.map((shell) => shell.id));
  const extensionOnly = extension.filter((shell) => !baseIds.has(shell.id));

  if (surface === "floating-toolbar") {
    return insertStaticShellsAfter(mergedBase, "copy-url", extensionOnly);
  }

  const extensionIds = new Set(extension.map((shell) => shell.id));
  const baseFiltered = mergedBase.filter(
    (shell) => !extensionIds.has(shell.id),
  );
  // The extension block leads, but the destructive section (hide / delete)
  // must stay LAST like every other node menu — without this, the extension's
  // trailing destructive entries would land mid-menu with the base block
  // (copy / z-order) appended after them.
  const merged = [...extension, ...baseFiltered];
  const isDestructive = (shell: StaticActionShell) =>
    getObjectActionDefinition(shell.id).group === "destructive";
  return [
    ...merged.filter((shell) => !isDestructive(shell)),
    ...merged.filter(isDestructive),
  ];
}
